import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import React, { useState, useRef, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const FAQSection = ({ section, theme, highlightedField }) => {
  const [expanded, setExpanded] = useState({})
  const [animatingItems, setAnimatingItems] = useState({})
  const timeoutRefs = useRef({})

  const isHighlighted = highlightedField === `section-${section.id}`

  // Get FAQ data from section settings
  const faqItems = section?.settings?.faqs || []

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const backgroundColor = themeColors.bgLight || "#FFFFFF"
  const textColor = theme?.colors?.textDark || "#1F2937"

  useEffect(
    () => () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout)
    },
    []
  )

  const handleToggle = (question) => {
    setExpanded((prev) => {
      const newExpanded = { ...prev, [question]: !prev[question] }
      if (!newExpanded[question]) {
        setAnimatingItems((prev) => ({ ...prev, [question]: true }))
        timeoutRefs.current[question] = setTimeout(() => {
          setAnimatingItems((prev) => ({ ...prev, [question]: false }))
        }, 300)
      }
      return newExpanded
    })
  }

  // Always render the section, even if no FAQs

  return (
    <section
      className={twMerge("py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: backgroundColor || theme?.colors?.bgLight || "#FFFFFF" }}>
      <AnimatedSection className="mx-auto max-w-4xl px-4">
        <div className="space-y-8">
          {section.headline && (
            <h2
              className="text-center"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                fontSize: "40px",
                lineHeight: "48px",
                color: textColor
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="text-center text-lg lg:text-xl"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: textColor,
                opacity: 0.8
              }}>
              {section.subheadline}
            </p>
          )}

          <div className="space-y-6">
            {faqItems && faqItems.length > 0 ? (
              faqItems.map((item, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <div
                    className="flex cursor-pointer items-center justify-between gap-2"
                    onClick={() => handleToggle(item.question)}>
                    <div
                      className="flex-1 text-xl font-bold"
                      style={{
                        fontFamily: theme?.fonts?.heading || "serif",
                        fontWeight: theme?.fonts?.headingWeight || "700",
                        color: textColor
                      }}>
                      {item.question}
                    </div>
                    {expanded[item.question] || animatingItems[item.question] ? (
                      <ChevronUpIcon className="h-6 w-6 flex-none text-teal" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 flex-none text-teal" />
                    )}
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      expanded[item.question] ? "max-h-96" : "max-h-0"
                    }`}>
                    <div
                      className="prose prose-lg max-w-none pt-4 text-[20px]"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "sans-serif",
                        color: textColor,
                        opacity: 0.8
                      }}
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <p
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "sans-serif",
                    color: textColor,
                    opacity: 0.6
                  }}>
                  No frequently asked questions have been added yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

export default FAQSection
